import {
  ChevronLeftIcon,
  MicrophoneIcon,
  PlusCircleIcon,
  StopIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { convert } from "html-to-text";
import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { BookContext } from "../../../contexts/BookContext";
import { ProgressCircle } from "../../Badges/ProgressCircle";
import { PlayButton } from "../../Buttons/PlayButton";
import { LocalFile } from "./CustomVoiceEditor";

type Props = {
  files: LocalFile[];
  setFiles: (files: LocalFile[]) => void;
  createVoice: () => void;
};

export const AudioSampleUploader: FC<Props> = ({
  files,
  setFiles,
  createVoice,
}) => {
  const { t } = useTranslation();
  const [voiceRecorder, setVoiceRecorder] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const MAX_FILES = 2;


  const { generated_chapters } = useContext(BookContext);

  const htmlSample = generated_chapters[0].content
    .split(". ")
    .slice(0, 5)
    .join(". ");

  const textSample = convert(htmlSample, { wordwrap: 130 });

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: "audio/mp3",
        });

        // Add to files
        const audioFile = new File([audioBlob], `recording-${Date.now()}.mp3`, {
          type: "audio/mp3",
        }) as LocalFile;
        audioFile.url = URL.createObjectURL(audioBlob);

        setFiles([...files, audioFile]);
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingDuration(0);

      // Duration counter
      intervalRef.current = setInterval(() => {
        setRecordingDuration((prev) => prev + 1);
      }, 1000);

      // Auto stop after 30 seconds
      timeoutRef.current = setTimeout(() => {
        stopRecording();
      }, 30000);
    } catch (error) {
      console.error("Error starting recording:", error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    }
  };

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const availableSlots = MAX_FILES - files.length;
      if (availableSlots <= 0) return;

      const filesToAdd = acceptedFiles.slice(0, availableSlots);
      const newFiles = filesToAdd.map((file: File) => ({
        ...file,
        url: URL.createObjectURL(file),
        name: file.name,
      })) as LocalFile[];

      setFiles([...files, ...newFiles]);
    },
    [files, setFiles]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: { "audio/*": [".mp3", ".wav"] },
    disabled: files.length >= MAX_FILES,
  });

  const isMaxFilesReached = files.length >= MAX_FILES;

  return (
    <>
      {voiceRecorder ? (
        <div className="w-full sm:w-fit h-fit overflow-x-hidden flex-col space-y-4 mx-2 sm:mx-6 bg-delicate-50 p-3 sm:p-2 border-2 border-dashed rounded-lg">
          <div className="flex flex-row items-center justify-between w-full">
            <button
              onClick={() => setVoiceRecorder(false)}
              className="text-white rounded bg-gray-400 py-1 px-2 hover:bg-gray-500 focus:outline-none flex items-center text-sm sm:text-base"
            >
              <ChevronLeftIcon className="h-4 w-4 sm:h-5 sm:w-5 inline-block mr-1" />
              {t("back")}
            </button>
          </div>

          <div className="w-full flex flex-row bg-white p-3 sm:p-4 h-32 sm:h-1/2 rounded overflow-y-auto">
            <p className="text-sm sm:text-base">{textSample}</p>
          </div>

          <div className="w-full flex flex-col items-center justify-center px-2 sm:px-4 rounded">
            <p className="text-rust-300 text-sm sm:text-base text-center">
              {t("audioBook.readASample")}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center w-full space-y-3 sm:space-y-0">
              <div className="hidden sm:flex sm:flex-1"></div>

              <div className="flex flex-col items-center">
                <button
                  onClick={isRecording ? stopRecording : startRecording}
                  disabled={isMaxFilesReached}
                  className={`p-3 sm:p-4 rounded-full ${
                    isRecording
                      ? "bg-red-500 hover:bg-red-600"
                      : "bg-rust-300 hover:bg-rust-600"
                  } text-white disabled:bg-gray-300`}
                >
                  {isRecording ? (
                    <StopIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                  ) : (
                    <MicrophoneIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                  )}
                </button>
              </div>

              <div className="flex-1 text-center mt-2 sm:mt-0">
                <span className="text-base sm:text-lg font-mono">
                  {formatTime(recordingDuration)} / 00:30
                </span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div
          {...getRootProps()}
          className={`w-full bg-delicate-50 flex-col space-y-4 h-64 sm:h-2/3 mx-2 sm:mx-6 p-3 sm:p-4 border-2 border-dashed rounded-lg cursor-pointer flex items-center justify-center focus:outline-none ${
            isMaxFilesReached
              ? "border-gray-300 bg-gray-50 cursor-not-allowed"
              : "border-delicate-300 hover:border-delicate-400 active:border-delicate-500"
          }`}
        >
          <p className="font-bold text-base sm:text-lg text-center">
            {isMaxFilesReached
              ? t("audioBook.maxFilesReached") || "Maximum de fichiers atteint"
              : t("audioBook.importSample")}
          </p>
          <PlusCircleIcon
            className={`h-10 w-10 sm:h-12 sm:w-12 ml-2 ${
              isMaxFilesReached ? "text-gray-400" : "text-rust-300"
            }`}
          />
          <p
            className={`text-xs sm:text-sm w-full sm:w-1/3 text-center px-2 ${
              isMaxFilesReached ? "text-gray-400" : "text-rust-300"
            }`}
          >
            {isMaxFilesReached
              ? `${files.length}/${MAX_FILES} fichiers ajoutés`
              : t("audioBook.importSampleDescription")}
          </p>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (!isMaxFilesReached) {
                setVoiceRecorder(true);
              }
            }}
            disabled={isMaxFilesReached}
            className={`text-lg text-bold py-2 px-4 rounded focus:outline-none ${
              isMaxFilesReached
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-delicate-800 text-white hover:bg-delicate-600"
            }`}
          >
            {t("audioBook.recordMyVoice")}
          </button>
          <input {...getInputProps()} />
        </div>
      )}

      <div className="flex flex-col border-t justify-center mt-4 w-full">
        {files.map((file) => (
          <div
            key={file.name}
            className="flex flex-row items-center border-b justify-between bg-white p-4 w-full"
          >
            <div className="flex flex-col items-center justify-between text-sm text-slate-800 text-semibold">
              {file.name}
            </div>
            <div className="flex flex-col items-center justify-between">
              <div className="flex flex-row items-center space-x-6">
                <PlayButton audioUrl={file.url} size={6} iconSize={4} />
                <TrashIcon
                  className="h-6 w-6 text-slate-500 cursor-pointer"
                  onClick={() =>
                    setFiles(files.filter((f) => f.name !== file.name))
                  }
                />
              </div>
            </div>
          </div>
        ))}
        <div className="flex flex-row items-center justify-between bg-white p-4 w-full">
          <div className="flex flex-row items-center justify-between text-sm text-slate-800 text-semibold">
            <ProgressCircle
              percent={(files.length / MAX_FILES) * 100}
              size="xxs"
              showPercent={false}
            />
            <p>{t("audioBook.fileLength")}</p>
          </div>
          <div className="flex flex-col items-center justify-between text-sm text-slate-800 text-semibold">
            <button
              onClick={createVoice}
              className="px-4 py-2 text-md bg-rust-300 rounded text-white hover:bg-rust-600 focus:outline-none"
            >
              {t("audioBook.generateVoice")}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
